import { useState, useEffect, useCallback } from 'react';
import { api, getCurrentMonth } from '../lib/api';

// Hook for fetching dashboard data
export const useDashboard = (month = getCurrentMonth()) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/dashboard/${month}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

// Hook for invoices
export const useInvoices = (month = null) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const url = month ? `/invoices?month=${month}` : '/invoices';
      const response = await api.get(url);
      setInvoices(response.data);
    } catch (err) {
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return { invoices, loading, refetch: fetchInvoices };
};

// Hook for losses
export const useLosses = (month = null) => {
  const [losses, setLosses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLosses = useCallback(async () => {
    try {
      setLoading(true);
      const url = month ? `/losses?month=${month}` : '/losses';
      const response = await api.get(url);
      setLosses(response.data);
    } catch (err) {
      console.error('Error fetching losses:', err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchLosses();
  }, [fetchLosses]);

  return { losses, loading, refetch: fetchLosses };
};

// Hook for manual orders
export const useOrders = (month = null) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const url = month ? `/orders?month=${month}` : '/orders';
      const response = await api.get(url);
      setOrders(response.data);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return { orders, loading, refetch: fetchOrders };
};

// Hook for budget
export const useBudget = (month = getCurrentMonth()) => {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBudget = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/budget/${month}`);
      setBudget(response.data);
    } catch (err) {
      console.error('Error fetching budget:', err);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    fetchBudget();
  }, [fetchBudget]);

  return { budget, loading, refetch: fetchBudget };
};

// Hook for dishes
export const useDishes = () => {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDishes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/dishes');
      setDishes(response.data);
    } catch (err) {
      console.error('Error fetching dishes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDishes();
  }, [fetchDishes]);

  return { dishes, loading, refetch: fetchDishes };
};

// Hook for menu
export const useMenu = () => {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMenu = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/menu');
      setMenu(response.data);
    } catch (err) {
      console.error('Error fetching menu:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  return { menu, loading, refetch: fetchMenu };
};

// Hook for salaries
export const useSalaries = () => {
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSalaries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/salaries');
      setSalaries(response.data);
    } catch (err) {
      console.error('Error fetching salaries:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  return { salaries, loading, refetch: fetchSalaries };
};

// Hook for fixed costs
export const useFixedCosts = () => {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/fixed-costs');
      setCosts(response.data);
    } catch (err) {
      console.error('Error fetching fixed costs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  return { costs, loading, refetch: fetchCosts };
};

// Hook for products (for loss price lookup)
export const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/products');
      setProducts(response.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return { products, loading, refetch: fetchProducts };
};
